import React, { useState } from 'react';
import {
  Box,
  Paper,
  useTheme,
  useMediaQuery,
  IconButton,
  Drawer,
  Tooltip,
} from '@mui/material';
import {
  History as HistoryIcon,
  Close as CloseIcon,
} from '@mui/icons-material';
import { styled } from '@mui/material/styles';
import AIAssistantChat from './AIAssistantChat';
import ConversationHistory from './ConversationHistory';
import { v4 as uuidv4 } from 'uuid';
import api from '../services/api';

const AssistantContainer = styled(Paper)(({ theme }) => ({
  height: '100%',
  display: 'flex',
  overflow: 'hidden',
  position: 'relative',
}));

const HistoryPanel = styled(Box)(({ theme }) => ({
  width: 320,
  flexShrink: 0,
  [theme.breakpoints.down('md')]: {
    width: 280,
  },
}));

const ChatPanel = styled(Box)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  minWidth: 0, // Prevent flex item from overflowing
}));

const MobileHistoryToggle = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 8,
  left: 8,
  zIndex: 1,
  [theme.breakpoints.up('md')]: {
    display: 'none',
  },
}));

const AIAssistantWithHistory = ({ onClose, isEmbedded = false }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [currentConversationId, setCurrentConversationId] = useState(() => uuidv4());
  const [conversationMessages, setConversationMessages] = useState({});
  const [mobileHistoryOpen, setMobileHistoryOpen] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleConversationSelect = async (conversationId) => {
    if (conversationId === currentConversationId) {
      return; // Already selected
    }

    try {
      setLoading(true);
      
      // If we haven't loaded this conversation's messages yet, fetch them
      if (!conversationMessages[conversationId]) {
        const response = await api.assistantApi.getConversationHistory(conversationId);
        const messages = response.data.messages || [];
        
        // Convert database messages to frontend format
        const formattedMessages = messages
          .filter(msg => msg.MessageRole === 'user' || msg.MessageRole === 'assistant') // Only show user and assistant messages in UI
          .filter(msg => msg.MessageContent !== 'Tool call initiated') // Exclude tool call initiated messages
          .map(msg => {
            let content = msg.MessageContent;
            let files = undefined;
            
            // Try to parse JSON content for multimodal messages
            try {
              const parsed = JSON.parse(content);
              if (Array.isArray(parsed)) {
                // Extract text from multimodal content
                const textParts = parsed.filter(item => item.type === 'text');
                content = textParts.map(item => item.text).join(' ');
                
                // Check if there were images
                const hasImages = parsed.some(item => item.type === 'image_url');
                if (hasImages) {
                  files = [{ name: 'Images uploaded', type: 'image' }];
                }
              }
            } catch (e) {
              // Not JSON, use as-is
            }
            
            return {
              id: msg.MessageID || Date.now() + Math.random(),
              text: content,
              isUser: msg.MessageRole === 'user',
              timestamp: new Date(msg.CreatedDate),
              files: files,
            };
          });
        
        // Add initial greeting if no messages
        if (formattedMessages.length === 0) {
          formattedMessages.unshift({
            id: 1,
            text: "Hello! I'm your AI assistant. I can help you with questions about your projects, data, and CRM system. You can also upload images and PDFs for me to analyze. Additionally, I can create new forms, users, projects, and quotes, etc. for you. How can I assist you today?",
            isUser: false,
            timestamp: new Date(),
          });
        }
        
        setConversationMessages(prev => ({
          ...prev,
          [conversationId]: formattedMessages
        }));
      }
      
      setCurrentConversationId(conversationId);
      
      // Close mobile history drawer
      if (isMobile) {
        setMobileHistoryOpen(false);
      }
      
    } catch (error) {
      console.error('Error loading conversation:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleNewConversation = () => {
    const newConversationId = uuidv4();
    setCurrentConversationId(newConversationId);
    
    // Don't pre-populate messages for new conversations
    // Let AIAssistantChat handle the initial greeting
    
    // Close mobile history drawer
    if (isMobile) {
      setMobileHistoryOpen(false);
    }
  };

  const handleRefreshHistory = () => {
    // This will trigger a refresh in the ConversationHistory component
    // The component handles its own refresh logic
  };

  const historyComponent = (
    <ConversationHistory
      selectedConversationId={currentConversationId}
      onConversationSelect={handleConversationSelect}
      onNewConversation={handleNewConversation}
      onRefresh={handleRefreshHistory}
    />
  );

  return (
    <AssistantContainer elevation={isEmbedded ? 3 : 1}>
      {/* Mobile history toggle button */}
      {isMobile && (
        <MobileHistoryToggle>
          <Tooltip title="Chat History">
            <IconButton 
              onClick={() => setMobileHistoryOpen(true)}
              sx={{ 
                backgroundColor: 'background.paper',
                boxShadow: 1,
                '&:hover': {
                  backgroundColor: 'background.paper',
                }
              }}
            >
              <HistoryIcon />
            </IconButton>
          </Tooltip>
        </MobileHistoryToggle>
      )}

      {/* Desktop history panel */}
      {!isMobile && (
        <HistoryPanel>
          {historyComponent}
        </HistoryPanel>
      )}

      {/* Mobile history drawer */}
      {isMobile && (
        <Drawer
          anchor="left"
          open={mobileHistoryOpen}
          onClose={() => setMobileHistoryOpen(false)}
          ModalProps={{
            keepMounted: true, // Better open performance on mobile
          }}
          PaperProps={{
            sx: { width: 320 }
          }}
        >
          <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <Box sx={{ p: 1, display: 'flex', justifyContent: 'flex-end' }}>
              <IconButton onClick={() => setMobileHistoryOpen(false)}>
                <CloseIcon />
              </IconButton>
            </Box>
            <Box sx={{ flex: 1 }}>
              {historyComponent}
            </Box>
          </Box>
        </Drawer>
      )}

      {/* Chat panel */}
      <ChatPanel>
        <AIAssistantChat
          onClose={onClose}
          isEmbedded={isEmbedded}
          conversationId={currentConversationId}
          onNewConversation={handleNewConversation}
          initialMessages={conversationMessages[currentConversationId]}
          loading={loading}
        />
      </ChatPanel>
    </AssistantContainer>
  );
};

export default AIAssistantWithHistory;
